import { useEffect, useState } from "react";

import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import Stack from "@/tpo/Stack";
import { useLazyQuery, useQuery } from "@apollo/client";

import DiagramModal from "./DiagramModal";
import MarkersAssessedChart from "./MarkersAssessedChart";

const DEFAULT_PAGE_SIZE = 10;

export default function InterpretationTabContent({
  reportResultsQuery,
  reportResultsQueryVariables,
  ListCard,
  listCardQuery,
  listCardQueryVariables
}) {
  const [activeDiagram, setActiveDiagram] = useState(null);

  const [loadResultSnps, { data: resultSnpsData, loading: resultSnpsLoading }] = useLazyQuery(
    listCardQuery,
    {
      fetchPolicy: "cache-first"
    }
  );

  useEffect(() => {
    if (!activeDiagram?.id) return;

    loadResultSnps({
      variables: {
        userResultId: activeDiagram.id,
        ...listCardQueryVariables
      }
    });
  }, [activeDiagram?.id, listCardQuery, listCardQueryVariables, loadResultSnps]);

  const baseVariables = {
    first: DEFAULT_PAGE_SIZE,
    after: "",
    ...reportResultsQueryVariables
  };

  const { data: reportResultsData, fetchMore } = useQuery(reportResultsQuery, {
    variables: baseVariables,
    skip: !reportResultsQuery || !baseVariables?.subsectorSlug
  });

  const reportDnaCounts = reportResultsData?.userReport?.dnaDatapointCounts;
  const reportFunctionalCounts = reportResultsData?.userReport?.functionalDatapointCounts;
  const reportSymptomCounts = reportResultsData?.userReport?.questionsAndAnswersCounts;

  const resultSnpsLoaded =
    Boolean(activeDiagram?.id) &&
    !resultSnpsLoading &&
    resultSnpsData?.userResult?.id === activeDiagram.id;

  const diagramSnpsForDiagram = resultSnpsLoaded
    ? resultSnpsData.userResult.snpsForDiagram || {}
    : {};
  const diagramExpectedSnps = resultSnpsLoaded
    ? resultSnpsData.userResult.expectedSnps || {}
    : {};

  return (
    <>
      <MarkersAssessedChart
        snpCounts={{
          inRange: reportDnaCounts?.inRangeCount || 0,
          outOfRange: reportDnaCounts?.outOfRangeCount || 0,
          untested: reportDnaCounts?.untestedCount || 0
        }}
        functionalCounts={{
          inRange: reportFunctionalCounts?.inRangeCount || 0,
          outOfRange: reportFunctionalCounts?.outOfRangeCount || 0,
          untested: reportFunctionalCounts?.untestedCount || 0
        }}
        symptomCounts={{
          inRange: reportSymptomCounts?.inRangeCount || 0,
          outOfRange: reportSymptomCounts?.outOfRangeCount || 0,
          untested: reportSymptomCounts?.untestedCount || 0
        }}
        sx={{
          maxWidth: 720,
          width: "100%",
          mx: "auto"
        }}
      />
      <Stack gap={40} mt={40}>
        <InfiniteScroll
          fetchMore={fetchMore}
          hasMore={reportResultsData?.userReport?.userResultConnections?.pageInfo?.hasNextPage}
          variables={{
            ...baseVariables,
            after: reportResultsData?.userReport?.userResultConnections?.pageInfo?.endCursor || ""
          }}
          next={() => {
            const endCursor =
              reportResultsData?.userReport?.userResultConnections?.pageInfo?.endCursor;
            if (endCursor) {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: 10
                }
              });
            }
          }}
          items={reportResultsData?.userReport?.userResultConnections?.edges?.map(
            edge => edge.node
          )}
        >
          <InfiniteScrollContext.Consumer>
            {({ itemsList, setBottomElement }) => (
              <>
                {itemsList.slice(0, -1).map(item => (
                  <ListCard
                    key={item.id}
                    id={item.id}
                    title={item.result.name}
                    description={item.result.description}
                    dnaDatapointCounts={item.dnaDatapointCounts}
                    functionalDatapointCounts={item.functionalDatapointCounts}
                    symptomCounts={item.symptomCounts}
                    onOpenDiagram={setActiveDiagram}
                    diagramName={item.result.diagram}
                    query={listCardQuery}
                    queryVariables={listCardQueryVariables}
                  />
                ))}
                {itemsList.slice(-1).map(item => (
                  <ListCard
                    key={item.id}
                    id={item.id}
                    title={item.result.name}
                    description={item.result.description}
                    dnaDatapointCounts={item.dnaDatapointCounts}
                    functionalDatapointCounts={item.functionalDatapointCounts}
                    symptomCounts={item.symptomCounts}
                    onOpenDiagram={setActiveDiagram}
                    diagramName={item.result.diagram}
                    query={listCardQuery}
                    queryVariables={listCardQueryVariables}
                    ref={setBottomElement}
                  />
                ))}
              </>
            )}
          </InfiniteScrollContext.Consumer>
        </InfiniteScroll>
      </Stack>
      <DiagramModal
        show={Boolean(activeDiagram)}
        close={() => setActiveDiagram(null)}
        diagramName={activeDiagram?.diagramName}
        title={activeDiagram?.title}
        snpsForDiagram={diagramSnpsForDiagram}
        expectedSnps={diagramExpectedSnps}
        loading={Boolean(activeDiagram) && !resultSnpsLoaded}
      />
    </>
  );
}
